import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'
import type {
  AssignCrewBody,
  ImportWttBody,
  ImportWttResult,
  OperatingDay,
  Service,
  ServiceCrew,
  UpsertOperatingDayBody
} from '../types'

// Spec 15 hooks. Live alongside the v1 useServices hooks rather than
// replacing them — both UIs share the Services table.
//
// Cache-key conventions match the existing patterns: ['services', eventId]
// for the per-event service list, ['service-crew', serviceId] for crew,
// ['operating-days', eventId] for the per-day flags, and ['lookup', name]
// for the catalogues.

// ---- WTT import -----------------------------------------------------------

export function useImportWtt(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ImportWttBody): Promise<ImportWttResult> =>
      ApiClient.importWtt(eventId, body),
    onSuccess: () => {
      // Replace-all on the server; safest is a full invalidation of the
      // services list + every service's crew. Don't try to keep the cache
      // hot through a wipe.
      qc.invalidateQueries({ queryKey: ['services', eventId] })
      qc.invalidateQueries({ queryKey: ['service-crew'] })
    }
  })
}

// ---- ServiceCrew ----------------------------------------------------------

export function useServiceCrew(serviceId: number) {
  return useQuery({
    queryKey: ['service-crew', serviceId],
    queryFn: () => ApiClient.serviceCrew(serviceId),
    enabled: Number.isFinite(serviceId) && serviceId > 0,
    staleTime: 60_000
  })
}

export function useAssignServiceCrew(serviceId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: AssignCrewBody): Promise<ServiceCrew[]> =>
      ApiClient.assignServiceCrew(serviceId, body),
    onSuccess: (rows) => {
      qc.setQueryData<ServiceCrew[]>(['service-crew', serviceId], rows)
    }
  })
}

// ---- OperatingDays --------------------------------------------------------

export function useOperatingDays(eventId: number) {
  return useQuery({
    queryKey: ['operating-days', eventId],
    queryFn: () => ApiClient.operatingDays(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0,
    staleTime: 60_000
  })
}

export function useUpsertOperatingDay(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ date, body }: { date: string; body: UpsertOperatingDayBody }) =>
      ApiClient.upsertOperatingDay(eventId, date, body),
    onSuccess: (updated: OperatingDay) => {
      qc.setQueryData<OperatingDay[]>(['operating-days', eventId], (old) => {
        if (!old) return [updated]
        const idx = old.findIndex((d) => d.date === updated.date)
        if (idx === -1) return [...old, updated]
        const next = old.slice()
        next[idx] = updated
        return next
      })
    }
  })
}

// ---- Lookups --------------------------------------------------------------

export function useLocos() {
  return useQuery({
    queryKey: ['lookup', 'locos'],
    queryFn: ApiClient.locos,
    staleTime: 60 * 60 * 1000
  })
}

export function useStations() {
  return useQuery({
    queryKey: ['lookup', 'stations'],
    queryFn: ApiClient.stations,
    staleTime: 60 * 60 * 1000
  })
}

// ---- MERAC sync stub (spec 15 §2.5) ---------------------------------------
//
// Until spec 25 ships, this hook always returns "disabled". Lives here as
// the seam — when sync lands, swap the implementation; ServiceEditor's
// readOnly check doesn't change.
export function useMeracSync(_eventId: number): {
  enabled: boolean
  lastSyncedAt: string | null
} {
  return { enabled: false, lastSyncedAt: null }
}

// ---- v2 service shape helpers ---------------------------------------------

// nextServiceOrdinal — spec 15 §2.3. Returns the next "Service N" ordinal
// based on existing services. Used by ServiceEditor to seed the name on
// create.
export function nextServiceOrdinal(services: Service[]): number {
  const used = services
    .map((s) => /^Service (\d+)$/.exec(s.serviceLabel)?.[1])
    .filter((x): x is string => !!x)
    .map(Number)
  return (used.length ? Math.max(...used) : 0) + 1
}
